import styled from '@emotion/styled'
import { Add } from '@mui/icons-material'
import { Checkbox, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import { memo, useEffect, useState } from 'react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { activityHasActiveDiscountOfType, activityOffersStudentPricing } from '../../../pure-js/libs/DiscountHelper'
import { DiscountUserInputV1, DiscountUserInputV2, DiscountPricingConfig } from '../../../pure-js/types/DiscountTypes'
import { DanceRoles } from '../../../pure-js/types/GrooverTypes'
import { Activity, Client } from '../../../pure-js/types/GrooverTypes'
import { BorderRadixes } from '../enums/BorderRadixes'
import { REGISTER_PAGE_MAX_WIDTH } from '../enums/HardCodedValues'
import { EIGHT_PX, FOUR_PX, TWELVE_PX } from '../enums/Spacings'
import { CourseRegistrationFormValues, isValidEmail } from '../helpers/CourseRegistrationStepOneHelper'
import { State } from '../hooks/useAppState'
import { useGrooverText } from '../hooks/useGrooverText'
import { getGrooverText } from '../libs/GrooverTextRepository'
import Box from './Box'
import GrooverText from './GrooverText'
import { ContinueButton } from './StyledComponents'
import Spinner from './Spinner'
import { TextFieldWithLabelForm } from './TextFieldWithLabelForm'
import { PhoneInputWithFlag } from './PhoneInputWithFlag'
import iconArrow from '/public/iconArrow.svg'
import iconCompleted from '/public/iconCompleted.svg'
import { DiscountRegionBox } from './DiscountRegionBox'

type Props = {
  state: State
  client?: Client
  activity: Activity
  formProps: UseFormReturn<CourseRegistrationFormValues, any, CourseRegistrationFormValues>
  role: DanceRoles | null
  changeRole: (isPartnerRole: boolean, newRole: DanceRoles) => void
  hasPartner: boolean
  onNextStep: () => void
  setHasPartner: (hasPartner: boolean) => void
  onSubmit: (data: CourseRegistrationFormValues) => void
  onSetIsMainUserStudent: (isMainUserStudent: boolean) => void
  onSetIsMainUserSibling: (isMainUserSibling: boolean) => void
  onSetIsMainUserSenior: (isMainUserSenior: boolean) => void
  onSetDiscountType: (discountType: DiscountPricingConfig['registrationType']) => void
  onSetDiscountRegion: (region: string) => void
  isSingleRegistration?: boolean
  onMainUserPhoneCountryChange?: (country: string) => void
  isCheckingUnfinishedEnrollment?: boolean
}

// Type guard to check if the input is DiscountUserInputV2
const isDiscountUserInputV2 = (
  input: DiscountUserInputV1 | DiscountUserInputV2 | undefined
): input is DiscountUserInputV2 => {
  return input !== undefined && 'mainUser' in input
}

const CourseRegistrationStepOneNoPartnerForm = ({
  state,
  client,
  activity,
  onNextStep,
  formProps,
  role,
  changeRole,
  hasPartner,
  setHasPartner,
  onSubmit,
  onSetIsMainUserStudent,
  onSetIsMainUserSibling,
  onSetIsMainUserSenior,
  onSetDiscountType,
  onSetDiscountRegion,
  isSingleRegistration = false,
  onMainUserPhoneCountryChange,
  isCheckingUnfinishedEnrollment = false
}: Props) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const t = useGrooverText()

  const activityHasStudentDiscount = activityOffersStudentPricing(activity, client?.discounts)
  const activityHasSiblingDiscount = activityHasActiveDiscountOfType(activity, client?.discounts, 'sibling')
  const activityHasSeniorDiscount = activityHasActiveDiscountOfType(activity, client?.discounts, 'senior')

  // Handles resize for mobile/web resolutions
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Set type to 'single' when it's a single registration activity
  useEffect(() => {
    if (isSingleRegistration) {
      changeRole(false, DanceRoles.Single)
    }
  }, [isSingleRegistration, changeRole])

  useEffect(() => {
    // Hide + clear when the activity doesn't allow these discounts
    if (
      state.isRegistrationCompleted ||
      state.isRegistrationSubmitted ||
      !!state.obfuscatedEnrollment?.mainUser?.phone
    ) {
      return
    }

    if (!activityHasStudentDiscount && state.isMainUserStudent) {
      onSetIsMainUserStudent(false)
    }

    if (!activityHasSiblingDiscount && state.isMainUserSibling) {
      onSetIsMainUserSibling(false)
    }

    if (!activityHasSeniorDiscount && state.isMainUserSenior) {
      onSetIsMainUserSenior(false)
    }
  }, [
    activityHasStudentDiscount,
    activityHasSiblingDiscount,
    activityHasSeniorDiscount,
    state.isRegistrationCompleted,
    state.isRegistrationSubmitted,
    state.obfuscatedEnrollment?.mainUser?.phone,
    state.isMainUserStudent,
    state.isMainUserSibling,
    state.isMainUserSenior,
    onSetIsMainUserStudent,
    onSetIsMainUserSibling,
    onSetIsMainUserSenior
  ])

  const handleSelectStudentDiscount = () => {
    if (state.isRegistrationCompleted || state.isRegistrationSubmitted || state.obfuscatedEnrollment?.mainUser?.phone)
      return
    if (state.isMainUserStudent) return onSetIsMainUserStudent(false)
    onSetIsMainUserStudent(true)
    onSetIsMainUserSenior(false)
  }

  const handleSelectSiblingDiscount = () => {
    if (state.isRegistrationCompleted || state.isRegistrationSubmitted || state.obfuscatedEnrollment?.mainUser?.phone)
      return
    onSetIsMainUserSibling(!state.isMainUserSibling)
  }

  const handleSelectSeniorDiscount = () => {
    if (state.isRegistrationCompleted || state.isRegistrationSubmitted || state.obfuscatedEnrollment?.mainUser?.phone)
      return
    if (state.isMainUserSenior) return onSetIsMainUserSenior(false)
    onSetIsMainUserStudent(false)
    onSetIsMainUserSenior(true)
  }

  const getStudentStatus = () => {
    const input = state.obfuscatedEnrollment?.discountUserInput
    if (!input) return state.isMainUserStudent
    if (isDiscountUserInputV2(input)) {
      return input.mainUser?.isStudent || state.isMainUserStudent
    }
    return input.isStudent || state.isMainUserStudent
  }

  const getSiblingStatus = () => {
    const input = state.obfuscatedEnrollment?.discountUserInput
    if (!input) return state.isMainUserSibling
    if (isDiscountUserInputV2(input)) {
      return input.mainUser?.isSibling || state.isMainUserSibling
    }
    return state.isMainUserSibling
  }

  const getSeniorStatus = () => {
    const input = state.obfuscatedEnrollment?.discountUserInput
    if (!input) return state.isMainUserSenior
    if (isDiscountUserInputV2(input)) {
      return input.mainUser?.isSenior || state.isMainUserSenior
    }
    return input.isSenior || state.isMainUserSenior
  }

  // Check if region selection is required and if a region is selected
  const isRegionSelectionRequired = state.selectedActivities.some(
    (a) => a.type === 'Event' && a.regions && a.regions.length > 0
  )
  const selectedRegion = state.obfuscatedEnrollment?.discountRegion || state.discountRegion
  const hasRegionSelected = !!selectedRegion
  const hasRegionError = isRegionSelectionRequired && !hasRegionSelected

  return (
    <FormContainer fullWidth>
      <form>
        <Box fullWidth fullPadding spacing="16px">
          <Box direction="row" gap="15px">
            {state.isRegistrationCompleted ||
              (!!state.obfuscatedEnrollment?.mainUser && <img src={iconCompleted} width={24} height={24} />)}
            <GrooverText
              textKey={
                activity.allowChildRegistration
                  ? 'registrationHeadersAndStatusHeaderParent'
                  : 'registrationHeadersAndStatusHeaderAttendee'
              }
            />
          </Box>
          <Box fullWidth top bottom spacing="8px">
            <Controller
              control={formProps.control}
              name="name"
              render={({ field, fieldState }) => (
                <TextFieldWithLabelForm
                  disabled={
                    state.isRegistrationCompleted ||
                    state.isRegistrationSubmitted ||
                    !!state.obfuscatedEnrollment?.mainUser?.name
                  }
                  {...field}
                  value={
                    state.obfuscatedEnrollment.mainUser?.name ? state.obfuscatedEnrollment.mainUser?.name : field.value
                  }
                  fullWidth
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationStep1AttendeesRegNameHelper')}
                  autoComplete="name"
                />
              )}
            />
          </Box>
          <Box fullWidth top bottom spacing="8px">
            <Controller
              control={formProps.control}
              name="phoneNumber"
              render={({ field, fieldState }) => (
                <PhoneInputWithFlag
                  name="phoneNumber"
                  client={client}
                  disabled={
                    state.isRegistrationCompleted ||
                    state.isRegistrationSubmitted ||
                    !!state.obfuscatedEnrollment?.mainUser?.phone
                  }
                  value={
                    state.obfuscatedEnrollment.mainUser?.phone
                      ? state.obfuscatedEnrollment.mainUser?.phone
                      : field.value
                  }
                  onChange={field.onChange}
                  onCountryChange={onMainUserPhoneCountryChange}
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationStep1AttendeesRegPhoneNumberHelper')}
                  autoComplete="tel"
                  fullWidth
                />
              )}
            />
          </Box>
          <Box fullWidth top bottom spacing="8px">
            <Controller
              control={formProps.control}
              name="email"
              rules={{
                validate: (email) => {
                  if (!email) return false
                  return isValidEmail(email)
                }
              }}
              render={({ field, fieldState }) => (
                <TextFieldWithLabelForm
                  type="email"
                  disabled={
                    state.isRegistrationCompleted ||
                    state.isRegistrationSubmitted ||
                    !!state.obfuscatedEnrollment?.mainUser?.email
                  }
                  {...field}
                  value={
                    state.obfuscatedEnrollment.mainUser?.email
                      ? state.obfuscatedEnrollment.mainUser?.email
                      : field.value
                  }
                  fullWidth
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationStep1AttendeesRegEmailHelper')}
                  autoComplete="email"
                />
              )}
            />
          </Box>

          {!!client?.showAddressInRegistration && (
            <Box fullWidth top bottom spacing="8px">
              <Controller
                control={formProps.control}
                name="mainUserAddress"
                render={({ field, fieldState }) => (
                  <TextFieldWithLabelForm
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.mainUser?.address
                    }
                    {...field}
                    value={
                      state.obfuscatedEnrollment.mainUser?.address
                        ? state.obfuscatedEnrollment.mainUser?.address
                        : field.value
                    }
                    fullWidth
                    fieldState={fieldState}
                    placeholder={getGrooverText('registrationStep1AddressPlaceholder')}
                    autoComplete="street-address"
                  />
                )}
              />
            </Box>
          )}

          {isRegionSelectionRequired && (
            <Box>
              <DiscountRegionBox id="discount-region" spacing={EIGHT_PX} alignSelf="flex-start">
                <FormControl error={hasRegionError}>
                  <StyledRadioGroup
                    row
                    value={state.obfuscatedEnrollment?.discountRegion || state.discountRegion}
                    onChange={(e) => {
                      onSetDiscountRegion(e.target.value)
                    }}
                  >
                    {state.selectedActivities
                      .find((a) => a.type === 'Event' && a.regions && a.regions.length > 0)
                      ?.regions?.map((region) => (
                        <StyledFormControlLabel key={region} value={region} control={<StyledRadio />} label={region} />
                      ))}
                  </StyledRadioGroup>
                </FormControl>
              </DiscountRegionBox>
            </Box>
          )}

          {!!client?.showSsnInRegistration && (
            <Box fullWidth top bottom spacing="8px">
              <Controller
                control={formProps.control}
                name="mainUserSsn"
                render={({ field, fieldState }) => (
                  <TextFieldWithLabelForm
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.mainUser?.ssn
                    }
                    {...field}
                    value={
                      state.obfuscatedEnrollment.mainUser?.ssn ? state.obfuscatedEnrollment.mainUser?.ssn : field.value
                    }
                    fullWidth
                    fieldState={fieldState}
                    placeholder={t('registrationStep1AttendeesRegSocialSecurityNumber')}
                  />
                )}
              />
            </Box>
          )}

          <Box
            id="user-types-container"
            fullWidth
            direction="row"
            changeDirectionOnMobile
            align="center"
            justify="flex-start"
            top
            spacing={EIGHT_PX}
          >
            {!state.individualFlow && !isSingleRegistration && (
              <UserTypeBox id="user-types-roles" spacing={EIGHT_PX} alignSelf="flex-start">
                <FormControl>
                  <StyledRadioGroup
                    row
                    value={(state.obfuscatedEnrollment?.mainUser?.type || role || '') as string}
                    onChange={(e) => {
                      if (
                        state.isRegistrationCompleted ||
                        state.isRegistrationSubmitted ||
                        state.obfuscatedEnrollment?.mainUser?.phone
                      )
                        return
                      changeRole(false, e.target.value as DanceRoles)
                    }}
                  >
                    <StyledFormControlLabel
                      value={DanceRoles.Leader}
                      control={<StyledRadio />}
                      label={getGrooverText('registrationStep1AttendeesRegRoleLabelLeader')}
                      disabled={
                        state.isRegistrationCompleted ||
                        state.isRegistrationSubmitted ||
                        !!state.obfuscatedEnrollment?.mainUser?.phone
                      }
                    />
                    <StyledFormControlLabel
                      value={DanceRoles.Follower}
                      control={<StyledRadio />}
                      label={getGrooverText('registrationStep1AttendeesRegRoleLabelFollower')}
                      disabled={
                        state.isRegistrationCompleted ||
                        state.isRegistrationSubmitted ||
                        !!state.obfuscatedEnrollment?.mainUser?.phone
                      }
                    />
                    {!!client?.allowBothForCouples && (
                      <StyledFormControlLabel
                        value={DanceRoles.Both}
                        control={<StyledRadio />}
                        label={getGrooverText('registrationStep1AttendeesRegRoleLabelBoth')}
                        disabled={
                          state.isRegistrationCompleted ||
                          state.isRegistrationSubmitted ||
                          !!state.obfuscatedEnrollment?.mainUser?.phone
                        }
                      />
                    )}
                  </StyledRadioGroup>
                </FormControl>
                {!!formProps.formState.errors.role?.message && (
                  <RoleErrorText>{String(formProps.formState.errors.role.message)}</RoleErrorText>
                )}
              </UserTypeBox>
            )}
            <UserTypeDiscounts
              id="user-types-discounts"
              direction="row"
              alignSelf="flex-start"
              spacing={TWELVE_PX}
              gap={TWELVE_PX}
            >
              {activityHasStudentDiscount && !activity.allowChildRegistration && (
                <Box direction="row" align="center" gap={FOUR_PX}>
                  <Checkbox
                    disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                    checked={getStudentStatus()}
                    onClick={handleSelectStudentDiscount}
                  />
                  <GrooverText
                    textKey="registrationStep3ActivitiesStudentCheckboxLabel"
                    customerId={client?.id}
                    style={{ color: 'rgba(0, 11, 97, 1)' }}
                  />
                </Box>
              )}
              {activityHasSiblingDiscount && activity.allowChildRegistration && (
                <Box direction="row" align="center" gap={FOUR_PX}>
                  <Checkbox
                    disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                    checked={getSiblingStatus()}
                    onClick={handleSelectSiblingDiscount}
                  />
                  <GrooverText
                    textKey="registrationStep1SiblingDiscountCheckboxLabel"
                    customerId={client?.id}
                    style={{ color: 'rgba(0, 11, 97, 1)' }}
                  />
                </Box>
              )}
              {activityHasSeniorDiscount && !activity.allowChildRegistration && (
                <Box direction="row" align="center" gap={FOUR_PX}>
                  <Checkbox
                    disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                    checked={getSeniorStatus()}
                    onClick={handleSelectSeniorDiscount}
                  />
                  <GrooverText textKey="registrationStep3ActivitiesSeniorCheckboxLabel" />
                </Box>
              )}
            </UserTypeDiscounts>
          </Box>

          <Box
            id="bottom-container"
            fullWidth
            direction={windowWidth <= 768 ? 'column' : 'row'}
            align="center"
            justify="space-between"
            spacing={EIGHT_PX}
            top
          >
            <Box id="cta-button-container" direction="row" justify="space-between" align="center" fullWidth>
              {!state.isRegistrationCompleted &&
                !hasPartner &&
                !state.obfuscatedEnrollment?.mainUser?.phone &&
                !isSingleRegistration &&
                !state.individualFlow && (
                  <AddPartnerButton
                    isDisabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                    direction="row"
                    align="center"
                    onClick={
                      state.isRegistrationCompleted || state.isRegistrationSubmitted
                        ? undefined
                        : () => {
                            setHasPartner(true)
                            onSetDiscountType('couple')
                          }
                    }
                  >
                    <Add sx={{ fontSize: 20, marginRight: '8px', color: '#2e7d32' }} />
                    <GrooverText textKey="registrationCtactaAddPartner" />
                  </AddPartnerButton>
                )}
              {(!hasPartner || isSingleRegistration) &&
                !state.isRegistrationCompleted &&
                !state.obfuscatedEnrollment?.partnerUser?.phone && (
                  <ContinueButton
                    isDisabled={
                      isCheckingUnfinishedEnrollment ||
                      state.hasFormErrors ||
                      hasRegionError ||
                      (!state.individualFlow &&
                        !isSingleRegistration &&
                        !state.obfuscatedEnrollment?.mainUser?.type &&
                        !role)
                    }
                    disabled={
                      isCheckingUnfinishedEnrollment ||
                      state.hasFormErrors ||
                      hasRegionError ||
                      (!state.individualFlow &&
                        !isSingleRegistration &&
                        !state.obfuscatedEnrollment?.mainUser?.type &&
                        !role)
                    }
                    onClick={
                      state.obfuscatedEnrollment?.mainUser?.phone ? onNextStep : formProps.handleSubmit(onSubmit)
                    }
                  >
                    <GrooverText textKey="registrationCtactaContinue" />
                    {isCheckingUnfinishedEnrollment ? <Spinner /> : <img src={iconArrow} width={16} height={16} />}
                  </ContinueButton>
                )}
            </Box>
          </Box>
        </Box>
      </form>
    </FormContainer>
  )
}
const UserTypeDiscounts = styled(Box)`
  padding: 4px;
`
const UserTypeBox = styled(Box)`
  background-color: rgb(243, 242, 242);
  border-radius: 12px;
`

const RoleErrorText = styled.span`
  display: block;
  color: #d32f2f;
  padding: 0 12px 8px 12px;
  font-size: 0.875rem;
`

const FormContainer = styled(Box)`
  background-color: #fcfcfc;
  max-width: ${REGISTER_PAGE_MAX_WIDTH};
  box-shadow: 0px 0px 8px 0px #2422bb;
  border-radius: ${BorderRadixes.moderate};
`

const AddPartnerButton = styled(Box)<{ isDisabled?: boolean }>`
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  border: 1px solid #aaa;
  background-color: ${(props) => (props.isDisabled ? '#f5f5f5' : 'white')};
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  border-radius: ${BorderRadixes.button};
  display: flex;
  align-items: center;
  color: #2e7d32;
  padding: 8px 16px;

  &:hover {
    background-color: #f5f5f5;
  }
`

const StyledRadioGroup = muiStyled(RadioGroup)`
  gap: 6px;
`

const StyledFormControlLabel = muiStyled(FormControlLabel)`
  border-radius: 12px;
  margin: 0;
  padding: 8px 16px;
  color: black;
  
  &.Mui-checked {
    background-color: #0E0349;
    
    .MuiTypography-root {
      color: black;
    }
  }

  .MuiTypography-root {
    color: black;
  }
`

const StyledRadio = muiStyled(Radio)`
  padding: 4px;
`

export default memo(CourseRegistrationStepOneNoPartnerForm)
